import { call, put, takeLatest, all, select } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { appIntl } from 'utility/context/IntlGlobalProvider';
import * as services from '../services';
import * as actions from '../actions';
import * as schemas from '../schemas';

// ========== Connections Sagas ==========

function* fetchConnectionsSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.getConnectionService as any,
      action.payload || {},
      configActions
    );

    // La respuesta ya viene como array directamente en response.data
    const normalized = schemas.normalizeConnections(response.data);
    yield put(actions.fetchConnectionsSuccess(normalized));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al cargar conexiones';
    yield put(actions.fetchConnectionsFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* insertConnectionSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.insertConnectionService as any,
      action.payload,
      configActions
    );

    yield put(actions.insertConnectionSuccess(response.data.data));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al guardar conexión';
    yield put(actions.insertConnectionFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* updateConnectionSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.updateConnectionService as any,
      action.payload,
      configActions
    );

    yield put(actions.updateConnectionSuccess(response.data.data));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al actualizar conexión';
    yield put(actions.updateConnectionFailure(errorMessage));
    toast.error(errorMessage);
  }
}

// ========== Contacts Sagas ==========

function* fetchContactsSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(services.getContactService as any, action.payload, configActions);

    const normalized = schemas.normalizeContacts(response.data.data);
    yield put(actions.fetchContactsSuccess(normalized));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al cargar contactos';
    yield put(actions.fetchContactsFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* insertContactSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.insertContactService as any,
      action.payload,
      configActions
    );

    yield put(actions.insertContactSuccess(response.data.data));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al guardar contacto';
    yield put(actions.insertContactFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* updateContactSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.updateContactService as any,
      action.payload,
      configActions
    );

    // El backend devuelve la conversación completa con la info del contacto actualizada
    const normalized = schemas.normalizeSingleConversation(response.data.data);

    yield put(actions.updateContactSuccess(normalized));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al actualizar contacto';
    yield put(actions.updateContactFailure(errorMessage));
    toast.error(errorMessage);
  }
}

// ========== Conversations Sagas ==========

function* fetchConversationsSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    let response;
    if (action.payload.conversationsConnectionId) {
      response = yield call(
        services.getConversationsByConnectionService as any,
        { conversationsConnectionId: action.payload.conversationsConnectionId },
        configActions
      );
    } else if (action.payload.recipientId) {
      response = yield call(
        services.getConversationsByRecipientService as any,
        { recipientId: action.payload.recipientId },
        configActions
      );
    }

    const normalized = schemas.normalizeConversations(response.data);
    yield put(actions.fetchConversationsSuccess(normalized));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al cargar conversaciones';
    yield put(actions.fetchConversationsFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* insertConversationSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.insertConversationService as any,
      action.payload,
      configActions
    );

    yield put(actions.insertConversationSuccess(response.data.data));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al crear conversación';
    yield put(actions.insertConversationFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* updateConversationSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    // Obtener la conversación actual del estado antes de actualizar
    const state = yield select();
    const currentConversation = state.whatsapp.conversations.byId[action.payload.conversationId];

    const response = yield call(
      services.updateConversationService as any,
      action.payload,
      configActions
    );

    // Combinar la conversación actual con los campos actualizados
    const updatedConversation = {
      ...currentConversation,
      ...action.payload
    };

    yield put(actions.updateConversationSuccess(updatedConversation));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al actualizar conversación';
    yield put(actions.updateConversationFailure(errorMessage));
  }
}

// ========== Timeline Sagas ==========

function* fetchTimelineSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.getConversationsTimelineService as any,
      action.payload,
      configActions
    );

    const normalized = schemas.normalizeTimeline(response.data);

    yield put(actions.fetchTimelineSuccess(normalized));

    // Verificar si hay eventos scheduled cuyo tiempo ya pasó y actualizarlos
    const now = new Date();

    for (const callEvent of normalized.callEvents) {
      if (callEvent.status === 'scheduled' && callEvent.scheduledFor) {
        const scheduledTime = new Date(callEvent.scheduledFor);

        if (scheduledTime.getTime() <= now.getTime()) {
          // Extraer ID numérico del formato "CallChatEvent-123"
          const numericId = parseInt(callEvent.id.split('-')[1]);
          if (!isNaN(numericId)) {
            try {
              yield call(
                services.updateCallChatEventService as any,
                {
                  conversationsCallChatEventId: numericId,
                  eventType: 'in_progress',
                  status: 1
                },
                { onError: () => {}, onSuccess: () => {} }
              );

              // Despachar acción para actualizar el estado en Redux
              yield put(
                actions.updateCallEventStatusSuccess({
                  callEventId: numericId,
                  status: 'in_progress'
                })
              );
            } catch (error) {
              console.error(`Error al actualizar llamada ${numericId}:`, error);
            }
          }
        }
      }
    }

    // Limpiar alertas cuando se abre el chat
    const conversation = response.data.conversation?.[0];
    if (conversation && (conversation.UnreadCount > 0 || conversation.HasActiveAlert)) {
      yield put(
        actions.updateConversationRequest({
          conversationId: conversation.ConversationId,
          conversationsConnectionId: conversation.ConversationsConnectionId || 1,
          unreadCount: 0,
          hasActiveAlert: false,
          status: 1
        })
      );
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al cargar timeline';
    yield put(actions.fetchTimelineFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* loadMoreTimelineSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.getConversationsTimelineService as any,
      action.payload,
      configActions
    );

    const normalized = schemas.normalizeTimeline(response.data);

    // Verificar si hay eventos scheduled cuyo tiempo ya pasó y actualizarlos
    const now = new Date();
    for (const callEvent of normalized.callEvents) {
      if (callEvent.status === 'scheduled' && callEvent.scheduledFor) {
        const scheduledTime = new Date(callEvent.scheduledFor);
        if (scheduledTime.getTime() <= now.getTime()) {
          const numericId = parseInt(callEvent.id.split('-')[1]);
          if (!isNaN(numericId)) {
            try {
              yield call(
                services.updateCallChatEventService as any,
                {
                  conversationsCallChatEventId: numericId,
                  eventType: 'in_progress',
                  status: 1
                },
                { onError: () => {}, onSuccess: () => {} }
              );

              yield put(
                actions.updateCallEventStatusSuccess({
                  callEventId: numericId,
                  status: 'in_progress'
                })
              );
            } catch (error) {
              console.error(`Error al actualizar llamada ${numericId}:`, error);
            }
          }
        }
      }
    }

    yield put(actions.loadMoreTimelineSuccess(normalized));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al cargar más mensajes';
    yield put(actions.loadMoreTimelineFailure(errorMessage));
    toast.error(errorMessage);
  }
}

// ========== Messages Sagas ==========

function* sendMessageSaga(action: any): Generator<any, void, any> {
  try {
    // 1. Obtener configuración de WhatsApp Business
    const state = yield select();
    const connections = state.whatsapp?.connections?.byId || {};
    const connectionIds = state.whatsapp?.connections?.allIds || [];

    if (connectionIds.length === 0) {
      toast.error('No hay conexión de WhatsApp Business configurada');
      yield put(actions.sendMessageFailure('No hay conexión configurada'));
      return;
    }

    const connection = connections[connectionIds[0]];
    if (!connection.phoneNumberId || !connection.accessToken) {
      toast.error('Faltan credenciales de WhatsApp Business');
      yield put(actions.sendMessageFailure('Credenciales incompletas'));
      return;
    }

    // 2. Enviar mensaje a WhatsApp Business API primero
    // Si es sticker o audio con texto, enviar dos mensajes separados
    const isSticker = action.payload.mediaType === 'sticker';
    const isAudio = action.payload.mediaType === 'audio';
    const hasText = action.payload.messageContent && action.payload.messageContent.trim() !== '';

    if ((isSticker || isAudio) && hasText) {
      // Enviar primero el sticker/audio
      const mediaResponse = yield call(services.sendWhatsAppMessageService, {
        phoneNumberId: connection.phoneNumberId,
        accessToken: connection.accessToken,
        recipientPhoneNumber: action.payload.recipientId,
        messageContent: '',
        replyToMessageId: action.payload.replyToMessageId,
        mediaUrl: action.payload.mediaUrl,
        mediaType: action.payload.mediaType,
        mediaFileTitle: action.payload.mediaFileTitle
      });

      if (!mediaResponse.success) {
        toast.error(
          mediaResponse.error || `Error al enviar ${isSticker ? 'sticker' : 'audio'} a WhatsApp`
        );
        yield put(actions.sendMessageFailure(mediaResponse.error));
        return;
      }

      // Guardar sticker/audio en DB
      const configActions = {
        onError: () => {},
        onSuccess: () => {}
      };

      const mediaDbResponse = yield call(
        services.insertOutboundMessageService as any,
        {
          ...action.payload,
          messageContent: '',
          whatsAppMessageId: mediaResponse.whatsAppMessageId,
          messageStatus: 'sent',
          sentAt: new Date()
        },
        configActions
      );

      yield put(actions.sendMessageSuccess(mediaDbResponse.data));

      // Enviar luego el texto
      const textResponse = yield call(services.sendWhatsAppMessageService, {
        phoneNumberId: connection.phoneNumberId,
        accessToken: connection.accessToken,
        recipientPhoneNumber: action.payload.recipientId,
        messageContent: action.payload.messageContent,
        replyToMessageId: undefined,
        mediaUrl: undefined,
        mediaType: undefined,
        mediaFileTitle: undefined
      });

      if (!textResponse.success) {
        toast.error(textResponse.error || 'Error al enviar texto a WhatsApp');
        yield put(actions.sendMessageFailure(textResponse.error));
        return;
      }

      // Guardar texto en DB
      const textDbResponse = yield call(
        services.insertOutboundMessageService as any,
        {
          conversationId: action.payload.conversationId,
          conversationsConnectionId: action.payload.conversationsConnectionId,
          recipientId: action.payload.recipientId,
          recipientType: action.payload.recipientType,
          messageContent: action.payload.messageContent,
          whatsAppMessageId: textResponse.whatsAppMessageId,
          messageStatus: 'sent',
          sentByUserId: action.payload.sentByUserId,
          sentAt: new Date()
        },
        configActions
      );

      yield put(actions.sendMessageSuccess(textDbResponse.data));
    } else {
      // Envío normal (un solo mensaje)
      const whatsAppResponse = yield call(services.sendWhatsAppMessageService, {
        phoneNumberId: connection.phoneNumberId,
        accessToken: connection.accessToken,
        recipientPhoneNumber: action.payload.recipientId,
        messageContent: action.payload.messageContent,
        replyToMessageId: action.payload.replyToMessageId,
        mediaUrl: action.payload.mediaUrl,
        mediaType: action.payload.mediaType,
        mediaFileTitle: action.payload.mediaFileTitle
      });

      // 3. Solo guardar en DB si WhatsApp API respondió exitosamente
      if (!whatsAppResponse.success) {
        toast.error(whatsAppResponse.error || 'Error al enviar mensaje a WhatsApp');
        yield put(actions.sendMessageFailure(whatsAppResponse.error));
        return;
      }

      // 4. Guardar mensaje en base de datos con el whatsAppMessageId
      const configActions = {
        onError: () => {},
        onSuccess: () => {}
      };

      const response = yield call(
        services.insertOutboundMessageService as any,
        {
          ...action.payload,
          whatsAppMessageId: whatsAppResponse.whatsAppMessageId,
          messageStatus: 'sent',
          sentAt: new Date()
        },
        configActions
      );

      yield put(actions.sendMessageSuccess(response.data));
    }

    // Actualizar conversación en el backend para que el trigger/SignalR actualice el preview
    if (action.payload.conversationId) {
      const conversationId =
        typeof action.payload.conversationId === 'object'
          ? action.payload.conversationId.conversationId
          : action.payload.conversationId;

      // Obtener nombre del usuario del estado de auth
      const userName = state.auth?.user?.completeName || 'Tú';

      // Generar preview según el contenido
      let preview = '';
      if (action.payload.messageContent) {
        const content = action.payload.messageContent;
        preview = `${userName}: ${content.substring(0, 50)}${content.length > 50 ? '...' : ''}`;
      } else if (action.payload.mediaType) {
        // Generar preview para archivos sin texto
        const mediaTypeLabel =
          {
            image: '[image]',
            video: '[video]',
            audio: '[audio]',
            document: '[file]',
            sticker: '[sticker]'
          }[action.payload.mediaType] || '[file]';
        preview = `${userName}: ${mediaTypeLabel}`;
      }

      if (preview) {
        const currentTime = new Date().toISOString();

        const updateResponse = yield call(
          services.updateConversationService as any,
          {
            conversationId,
            conversationsConnectionId: action.payload.conversationsConnectionId || 1,
            lastMessagePreview: preview,
            lastMessageType: action.payload.mediaType || 'text',
            lastActivityAt: currentTime,
            hasActiveAlert: false,
            unreadCount: 0,
            status: 1
          },
          { onError: () => {}, onSuccess: () => {} }
        );
      }
    }
  } catch (error: any) {
    console.error('[sendMessageSaga] ❌ Error:', error);
    const errorMessage = error.response?.data?.message || 'Error al enviar mensaje';
    yield put(actions.sendMessageFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* updateMessageStatusSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    yield call(services.updateOutboundMessageService as any, action.payload, configActions);
  } catch (error: any) {
    console.error('Error al actualizar estado del mensaje:', error);
  }
}

function* markMessageReadSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    yield call(services.updateInboundMessageService as any, action.payload, configActions);
  } catch (error: any) {
    console.error('Error al marcar mensaje como leído:', error);
  }
}

// ========== Alerts Sagas ==========

function* insertChatAlertSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.insertChatAlertService as any,
      action.payload,
      configActions
    );

    yield put(actions.insertChatAlertSuccess(response.data.data));

    // Obtener datos de la alerta (backend regresa PascalCase)
    const alertData = response.data.data || response.data;
    const alertTime = alertData.CreatedAt || new Date().toISOString();

    // Actualizar el lastMessagePreview de la conversación con el código de traducción
    const updateConversationPayload = {
      conversationId: action.payload.conversationId,
      conversationsConnectionId: action.payload.conversationsConnectionId || 1,
      lastMessagePreview: '[emergency_alert]',
      lastActivityAt: alertTime,
      hasActiveAlert: false,
      unreadCount: 0,
      status: 1
    };

    yield call(services.updateConversationService as any, updateConversationPayload, {
      onError: () => {},
      onSuccess: () => {}
    });
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al crear alerta';
    yield put(actions.insertChatAlertFailure(errorMessage));
    toast.error(errorMessage);
  }
}

// ========== Call Events Sagas ==========

// Saga para crear llamada AI completa (CallLog + CallEvent)
function* createAICallSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    // 1. Crear CallLog primero
    const callLogPayload = {
      conversationId: action.payload.conversationId,
      contactPhoneNumber: action.payload.contactPhoneNumber,
      callerPhoneNumber: action.payload.callerPhoneNumber,
      callDirection: 'outbound',
      callStatus: 'scheduled',
      scheduledAt: action.payload.scheduledFor
    };

    const callLogResponse = yield call(
      services.insertCallLogService as any,
      callLogPayload,
      configActions
    );

    const callLogData = callLogResponse.data.data || callLogResponse.data;
    const conversationsCallLogId =
      callLogData.conversationsCallLogId || callLogData.ConversationsCallLogId;

    // 2. Crear CallEvent con el conversationsCallLogId
    const callEventPayload = {
      conversationId: action.payload.conversationId,
      conversationsCallLogId: conversationsCallLogId,
      eventType: 'scheduled',
      displayMessage: action.payload.displayMessage,
      scheduledFor: action.payload.scheduledFor
    };

    const callEventResponse = yield call(
      services.insertCallChatEventService as any,
      callEventPayload,
      configActions
    );

    yield put(
      actions.createAICallSuccess({
        callLog: callLogData,
        callEvent: callEventResponse.data.data || callEventResponse.data
      })
    );

    // Obtener datos del evento de llamada (backend regresa PascalCase)
    const eventData = callEventResponse.data.data || callEventResponse.data;
    const callTime = eventData.CreatedAt || new Date().toISOString();

    // Actualizar el lastMessagePreview de la conversación con el código de traducción
    const updateConversationPayload = {
      conversationId: action.payload.conversationId,
      conversationsConnectionId: action.payload.conversationsConnectionId || 1,
      lastMessagePreview: '[ai_call_scheduled]',
      lastActivityAt: callTime,
      hasActiveAlert: false,
      unreadCount: 0,
      status: 1
    };

    yield call(services.updateConversationService as any, updateConversationPayload, {
      onError: () => {},
      onSuccess: () => {}
    });
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al programar llamada AI';
    yield put(actions.createAICallFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* updateCallEventStatusSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const requestPayload = {
      conversationsCallChatEventId: action.payload.callEventId,
      eventType: action.payload.status,
      status: 1
    };

    const response = yield call(
      services.updateCallChatEventService as any,
      requestPayload,
      configActions
    );

    yield put(
      actions.updateCallEventStatusSuccess({
        callEventId: action.payload.callEventId,
        status: action.payload.status
      })
    );
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al actualizar estado de llamada';
    yield put(actions.updateCallEventStatusFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* insertCallEventSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.insertCallChatEventService as any,
      action.payload,
      configActions
    );

    yield put(actions.insertCallEventSuccess(response.data.data));

    // El backend actualizará el timeline vía SignalR cuando se procese el evento
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al crear evento de llamada';
    yield put(actions.insertCallEventFailure(errorMessage));
    toast.error(errorMessage);
  }
}
// ========== Messages After Last Event Saga ==========

function* getMessagesAfterLastEventSaga(action: any): Generator<any, void, any> {
  try {
    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    const response = yield call(
      services.getMessagesAfterLastEventService as any,
      action.payload,
      configActions
    );

    const messages = response.data || [];

    yield put(actions.getMessagesAfterLastEventSuccess(messages));
    const intl = appIntl();
    toast.success(
      intl.formatMessage({ id: 'whatsapp.summarizeSuccess' }, { count: messages.length })
    );
  } catch (error: any) {
    const intl = appIntl();
    const errorMessage =
      error.response?.data?.message || intl.formatMessage({ id: 'whatsapp.summarizeError' });
    yield put(actions.getMessagesAfterLastEventFailure(errorMessage));
    toast.error(errorMessage);
  }
}

// ========== Templates Sagas ==========

function* fetchTemplatesSaga(action: any): Generator<any, void, any> {
  try {
    const response = yield call(services.getWhatsAppTemplatesService, action.payload);

    if (response.success) {
      yield put(actions.fetchTemplatesSuccess(response.templates));
    } else {
      yield put(actions.fetchTemplatesFailure(response.error));
      toast.error(response.error);
    }
  } catch (error: any) {
    const intl = appIntl();
    const errorMessage =
      error.response?.data?.message || intl.formatMessage({ id: 'whatsapp.templatesError' });
    yield put(actions.fetchTemplatesFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* sendTemplateSaga(action: any): Generator<any, void, any> {
  try {
    const {
      conversationId: rawConversationId,
      template,
      recipientPhoneNumber,
      ...templateData
    } = action.payload;

    // Extraer conversationId si es un objeto
    const conversationId =
      typeof rawConversationId === 'object' ? rawConversationId.conversationId : rawConversationId;

    const response = yield call(services.sendWhatsAppTemplateService, {
      ...templateData,
      template,
      recipientPhoneNumber
    });

    if (response.success) {
      // Extraer el texto de la plantilla y reemplazar parámetros
      let templateText = `[Template: ${template.name}]`;
      if (template.components) {
        const bodyComponent = template.components.find((c: any) => c.type === 'BODY');
        if (bodyComponent && bodyComponent.text) {
          templateText = bodyComponent.text;

          // Reemplazar parámetros {{1}}, {{2}}, etc. con los valores reales
          if (templateData.components) {
            const bodyParams = templateData.components.find((c: any) => c.type === 'body');
            if (bodyParams && bodyParams.parameters) {
              bodyParams.parameters.forEach((param: any, index: number) => {
                const placeholder = `{{${index + 1}}}`;
                templateText = templateText.replace(
                  new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
                  param.text
                );
              });
            }
          }
        }
      }

      // Guardar mensaje en base de datos
      const state = yield select();
      const connections = state.whatsapp?.connections?.byId || {};
      const connectionIds = state.whatsapp?.connections?.allIds || [];
      const connection = connections[connectionIds[0]];
      const user = state.auth?.user;
      const sentByUserId = user?.authUserId ? Number(user.authUserId) : undefined;

      const configActions = {
        onError: () => {},
        onSuccess: () => {}
      };

      const dbResponse = yield call(
        services.insertOutboundMessageService as any,
        {
          conversationId,
          conversationsConnectionId: connection?.conversationsConnectionId || 1,
          recipientId: recipientPhoneNumber,
          recipientType: 'individual',
          messageContent: templateText,
          mediaType: 'text',
          whatsAppMessageId: response.whatsAppMessageId,
          messageStatus: 'sent',
          sentByUserId: sentByUserId,
          sentAt: new Date()
        },
        configActions
      );

      // Actualizar preview de conversación
      const userName = state.auth?.user?.completeName || 'Tú';
      const preview = `${userName}: ${templateText.substring(0, 50)}${templateText.length > 50 ? '...' : ''}`;
      const currentTime = new Date().toISOString();

      yield call(
        services.updateConversationService as any,
        {
          conversationId,
          conversationsConnectionId: connection?.conversationsConnectionId || 1,
          lastMessagePreview: preview,
          lastMessageType: 'text',
          lastActivityAt: currentTime,
          hasActiveAlert: false,
          unreadCount: 0,
          status: 1
        },
        { onError: () => {}, onSuccess: () => {} }
      );

      yield put(actions.sendTemplateSuccess(response));

      // Refresh timeline to show the sent template
      yield put(actions.fetchTimelineRequest(conversationId, 50));
    } else {
      yield put(actions.sendTemplateFailure(response.error));
      toast.error(response.error);
    }
  } catch (error: any) {
    console.error('[sendTemplateSaga] ❌ Error:', error);
    const intl = appIntl();
    const errorMessage =
      error.response?.data?.message || intl.formatMessage({ id: 'whatsapp.templateError' });
    yield put(actions.sendTemplateFailure(errorMessage));
    toast.error(errorMessage);
  }
}

// ========== Open Conversation By Phone Saga ==========

function* openConversationByPhoneSaga(action: any): Generator<any, void, any> {
  try {
    const { phoneNumber, contactName, driverId } = action.payload;

    const configActions = {
      onError: () => {},
      onSuccess: () => {}
    };

    // 1. Get first available connection
    const state = yield select();
    const connections = state.whatsapp.connections.allIds.map(
      (id: string) => state.whatsapp.connections.byId[id]
    );

    if (connections.length === 0) {
      toast.error('No hay conexiones de WhatsApp disponibles');
      yield put(actions.openConversationByPhoneFailure('No hay conexiones disponibles'));
      return;
    }

    const firstConnection = connections[0];
    const conversationsConnectionId = firstConnection.conversationsConnectionId;

    // 2. Clean phone number (remove all non-digits)
    const fullPhoneNumber = phoneNumber.replace(/\D/g, '');

    let conversationsContactId: number;

    // 3. Check if contact exists
    try {
      const existingContactResponse = yield call(
        services.getContactService as any,
        { phoneNumber: fullPhoneNumber },
        configActions
      );

      if (existingContactResponse.data && existingContactResponse.data.length > 0) {
        // Contact exists, use its ID
        conversationsContactId = existingContactResponse.data[0].ConversationsContactId;
      } else {
        throw new Error('Contact not found');
      }
    } catch (error: any) {
      // 4. Contact doesn't exist, create it
      const contactPayload = {
        phoneNumber: fullPhoneNumber,
        firstName: contactName || null,
        lastName: null,
        displayName: contactName || null,
        email: null,
        driverId: driverId || null,
        companyName: null,
        profilePictureUrl: null,
        notes: null
      };

      const contactResponse = yield call(
        services.insertContactService as any,
        contactPayload,
        configActions
      );

      conversationsContactId = contactResponse.data?.ConversationsContactId;

      if (!conversationsContactId) {
        throw new Error('No se pudo crear el contacto');
      }
    }

    // 5. Check if conversation already exists
    const existingConversationResponse = yield call(
      services.getConversationsByRecipientService as any,
      { recipientId: fullPhoneNumber },
      configActions
    );

    if (existingConversationResponse.data && existingConversationResponse.data.length > 0) {
      // Conversation exists, just pin it
      const existingConversation = existingConversationResponse.data[0];

      yield put(actions.pinConversation(existingConversation.ConversationId.toString()));

      // Refresh conversations list
      yield put(
        actions.fetchConversationsRequest({
          conversationsConnectionId
        })
      );

      yield put(actions.openConversationByPhoneSuccess(existingConversation));
      return;
    }

    // 6. Create new conversation
    const conversationPayload = {
      conversationsConnectionId,
      recipientType: 'individual',
      recipientId: fullPhoneNumber,
      conversationsContactId,
      groupName: null,
      groupDescription: null,
      lastActivityAt: new Date()
    };

    const conversationResponse = yield call(
      services.insertConversationService as any,
      conversationPayload,
      configActions
    );

    const newConversation = conversationResponse.data;

    // 7. Pin the conversation to show mini tile
    yield put(actions.pinConversation(newConversation.ConversationId.toString()));

    // 8. Fetch conversations to update state
    yield put(
      actions.fetchConversationsRequest({
        conversationsConnectionId
      })
    );

    yield put(actions.openConversationByPhoneSuccess(newConversation));
    toast.success('Conversación creada');
  } catch (error: any) {
    console.error('[openConversationByPhoneSaga] Error:', error);
    const errorMessage = error.response?.data?.message || 'Error al abrir conversación';
    yield put(actions.openConversationByPhoneFailure(errorMessage));
    toast.error(errorMessage);
  }
}

// ========== Root Saga ==========

export function* whatsappSaga() {
  yield all([
    // Connections
    takeLatest(actions.FETCH_CONNECTIONS_REQUEST, fetchConnectionsSaga),
    takeLatest(actions.INSERT_CONNECTION_REQUEST, insertConnectionSaga),
    takeLatest(actions.UPDATE_CONNECTION_REQUEST, updateConnectionSaga),

    // Contacts
    takeLatest(actions.FETCH_CONTACTS_REQUEST, fetchContactsSaga),
    takeLatest(actions.INSERT_CONTACT_REQUEST, insertContactSaga),
    takeLatest(actions.UPDATE_CONTACT_REQUEST, updateContactSaga),

    // Conversations
    takeLatest(actions.FETCH_CONVERSATIONS_REQUEST, fetchConversationsSaga),
    takeLatest(actions.INSERT_CONVERSATION_REQUEST, insertConversationSaga),
    takeLatest(actions.UPDATE_CONVERSATION_REQUEST, updateConversationSaga),

    // Timeline
    takeLatest(actions.FETCH_TIMELINE_REQUEST, fetchTimelineSaga),
    takeLatest(actions.LOAD_MORE_TIMELINE_REQUEST, loadMoreTimelineSaga),

    // Messages
    takeLatest(actions.SEND_MESSAGE_REQUEST, sendMessageSaga),
    takeLatest(actions.UPDATE_MESSAGE_STATUS, updateMessageStatusSaga),
    takeLatest(actions.MARK_MESSAGE_READ, markMessageReadSaga),

    // Alerts
    takeLatest(actions.INSERT_CHAT_ALERT_REQUEST, insertChatAlertSaga),

    // Call Events
    takeLatest(actions.INSERT_CALL_EVENT_REQUEST, insertCallEventSaga),
    takeLatest(actions.CREATE_AI_CALL_REQUEST, createAICallSaga),
    takeLatest(actions.UPDATE_CALL_EVENT_STATUS_REQUEST, updateCallEventStatusSaga),

    // Messages After Last Event
    takeLatest(actions.GET_MESSAGES_AFTER_LAST_EVENT_REQUEST, getMessagesAfterLastEventSaga),

    // Templates
    takeLatest(actions.FETCH_TEMPLATES_REQUEST, fetchTemplatesSaga),
    takeLatest(actions.SEND_TEMPLATE_REQUEST, sendTemplateSaga),

    // Open Conversation By Phone
    takeLatest(actions.OPEN_CONVERSATION_BY_PHONE_REQUEST, openConversationByPhoneSaga)
  ]);
}
